// src/components/SideMenu/SideMenu.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faClipboardList, faPlus, faUndo, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../ConfirmationModal';
import { SideMenuContainer, AddNewCharacterButton, SummarizeButton, CopyConfigButton, PasteConfigButton, ResetConfigButton, FooterContainer, FakeOptionContainer, VersionInfo, LoadingMessage, ScrollableCharactersContainer, LoadingDiv } from './SideMenuStyles';
import { MOBILE_BREAKPOINT } from '../../common/Constants';
import CharacterButton from '../CharacterButton';
import { ClipLoader } from 'react-spinners';

const SideMenu = ({
    aiCharactersConfig,
    setEditingConfig,
    setShowEditConfigModal,
    resetToDefaults,
    addNewCharacter,
    addNewResponse,
    deleteCharacter,
    summarizeResponses,
    setIsFakeResponse,
    isFakeResponse,
    userInput,
    version,
    importCharacterConfig,
    isLoading
}) => {
    // #region SideMenu Code
    // #region State
    const [showCopyConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showCopyFailedModal, setShowCopyFailedModal] = useState(false);
    const [showConfirmResetModal, setShowConfirmResetModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showConfirmPasteModal, setShowConfirmPasteModal] = useState(false);
    const [charactersImported, setCharactersImported] = useState(0);
    const [deleteCharacterName, setDeleteCharacterName] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const [showFakeOption] = useState(process.env.REACT_APP_SHOW_FAKE_OPTION === 'true');

    // #endregion State
    // #region Handlers
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const handleEditCharacterClick = (characterConfig) => {
        setEditingConfig(characterConfig);
        setShowEditConfigModal(true);
    };
    const handleShowConfirmDeleteCharacter = (name) => {
        ////console.debug('Deleting character:', name);
        setDeleteCharacterName(name);
        setShowConfirmDeleteModal(true);
    };
    const handleCopyToClipboard = () => {
        const configString = JSON.stringify(aiCharactersConfig, null, 2);
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(configString)
                .then(() => {
                    setShowConfirmationModal(true);
                })
                .catch(err => {
                    //console.error('Failed to copy configuration:', err);
                });
        }
        else {
            setShowCopyFailedModal(true);
        }
    };
    const handlePasteFromClipboard = async () => {
        if (navigator.clipboard && navigator.clipboard.readText) {
            await navigator.clipboard.readText()
                .then(text => {
                    try {
                        const parsedConfig = JSON.parse(text);
                        if (importCharacterConfig(parsedConfig)) {
                            setCharactersImported(parsedConfig.length);
                            setShowConfirmPasteModal(true);
                            return { success: true, message: 'Configuration successfully imported!' };
                        };
                    }
                    catch (err) {
                        //console.error('Failed to parse clipboard text:', err);
                    }
                })
                .catch(err => {
                    //console.error('Failed to read clipboard text:', err);
                });
        }
    };
    const handleConfirmResetToDefaults = () => {
        setShowConfirmResetModal(true);
    };
    const handleResetToDefaults = () => {
        resetToDefaults();
        setShowConfirmResetModal(false);
    };
    const handleAddNewResponseClick = (characterConfig) => {
        addNewResponse(characterConfig, userInput, isFakeResponse);
    };
    const handleSummarizeResponses = () => {
        summarizeResponses(userInput, isFakeResponse);
    };
    // #endregion Handlers
    // #endregion SideMenu Code
    return (
        <SideMenuContainer>
            <AddNewCharacterButton onClick={addNewCharacter}>
                <FontAwesomeIcon icon={faPlus} />
                {!isMobile && (<span>Add New Consultant</span>)}
                {isMobile && (<span>Add New</span>)}
            </AddNewCharacterButton>
            <ScrollableCharactersContainer>
                {isLoading ? (
                    <LoadingDiv>
                        <ClipLoader size={50} color={'#2196F3'} loading={isLoading} />
                        <LoadingMessage>Loading Consultants</LoadingMessage>
                    </LoadingDiv>
                ) : (
                    aiCharactersConfig
                        .filter(characterConfig => characterConfig.role !== 'Summarizer')
                        .map((characterConfig, index) => (
                            <CharacterButton
                                key={index}
                                characterConfig={characterConfig}
                                onAddNewResponseClick={handleAddNewResponseClick}
                                onEditCharacterClick={handleEditCharacterClick}
                                onAddNewCharacter={addNewCharacter}
                                onDeleteCharacter={handleShowConfirmDeleteCharacter}
                                isMobile={isMobile}
                            />
                        )))
                }
            </ScrollableCharactersContainer>
            <SummarizeButton data-testid='summarize-button' onClick={(e) => { e.stopPropagation(); handleSummarizeResponses(); }}>
                <FontAwesomeIcon icon={faClipboardList} />
                {!isMobile && (<span>Summarize Responses</span>)}
                {isMobile && (<span>Summarize</span>)}
            </SummarizeButton>
            <CopyConfigButton onClick={handleCopyToClipboard}>
                <FontAwesomeIcon icon={faClipboard} /><span>Copy {(!isMobile) && (" Config")}</span>
            </CopyConfigButton>
            <PasteConfigButton onClick={handlePasteFromClipboard}>
                <FontAwesomeIcon icon={faClipboardCheck} /><span>Paste {(!isMobile) && (" Config")}</span>
            </PasteConfigButton>
            <ResetConfigButton onClick={handleConfirmResetToDefaults}>
                <FontAwesomeIcon icon={faUndo} /> <span>Reset {(!isMobile) && (" to Defaults")}</span>
            </ResetConfigButton>
            <FooterContainer>
                {showFakeOption &&
                    <FakeOptionContainer>
                        <input data-testid="fake-option-checkbox" type="checkbox" checked={isFakeResponse} onChange={(e) => setIsFakeResponse(e.target.checked)} />
                        {(!isMobile) && ("Use ")}Fake it!
                    </FakeOptionContainer>
                }
                <VersionInfo>Version: {version}</VersionInfo>
            </FooterContainer>
            {showCopyConfirmationModal && (
                <ConfirmationModal
                    message="Your configuration has been copied to the clipboard!"
                    buttons={[
                        ['OK', () => { setShowConfirmationModal(false); }, 'primaryButton']
                    ]}
                />
            )}
            {showCopyFailedModal && (
                <ConfirmationModal
                    message="Your configuration has NOT been copied to the clipboard! The clipboard is not available on this device."
                    buttons={[
                        ['OK', () => { setShowCopyFailedModal(false); }, 'noButton']
                    ]}
                />
            )}
            {showConfirmResetModal && (
                <ConfirmationModal
                    message="Are you sure you want to reset the configuration to defaults? This action cannot be undone."
                    buttons={[
                        ['Yes', handleResetToDefaults, 'yesButton'],
                        ['No', () => { setShowConfirmResetModal(false); }, 'noButton']
                    ]}
                />
            )}
            {showConfirmDeleteModal && (
                <ConfirmationModal
                    data-testid={'confirmDeleteModal'}
                    message={`Are you sure you want to delete ${deleteCharacterName}? This action cannot be undone.`}
                    buttons={[
                        ['Yes', () => { deleteCharacter(deleteCharacterName); setShowConfirmDeleteModal(false); }, 'yesButton'],
                        ['No', () => { setShowConfirmDeleteModal(false); }, 'noButton']
                    ]}
                />
            )}
            {showConfirmPasteModal && (
                <ConfirmationModal
                    message={`${charactersImported} successfully imported!`}
                    buttons={[
                        ['OK', () => { setShowConfirmPasteModal(false); }, 'primaryButton']
                    ]}
                />
            )}
        </SideMenuContainer>
    );
};

export default SideMenu;
